<template>
    <div class="sp-shell">

        <!-- ── SHELL HEADER ── -->
        <div class="sp-header">
            <div class="sp-header-left">
                <span class="sp-header-title">{{ model ? $translateLabel(model.label) : $t('integrations.support.navLabel') }}</span>
                <template v-if="tierInfo">
                    <span class="sp-header-sep"></span>
                    <b-badge :variant="tierVariant(tierInfo.tier)" class="sp-tier-badge text-capitalize">
                        {{ $t('integrations.support.tiers.' + tierInfo.tier) }}
                    </b-badge>
                    <div v-if="tierInfo.limits" class="sp-quota-row">
                        <span
                            v-for="period in ['day', 'week', 'month']"
                            :key="period"
                            class="sp-quota-item"
                            :class="{ 'sp-quota-danger': isLimitHit(period) }"
                        >
                            <strong>{{ usageRemaining(period) }}</strong>
                            <em>{{ $t('integrations.support.per' + capitalize(period)) }}</em>
                        </span>
                    </div>
                </template>
            </div>
            <b-button v-if="view === 'list'" variant="primary" size="sm" @click="showCreateForm" :disabled="!canCreateTicket">
                <b-icon icon="plus" class="mr-1" />{{ $t('integrations.support.newTicket') }}
            </b-button>
        </div>

        <div v-if="limitReached && view === 'list'" class="sp-limit-banner">
            <b-icon icon="exclamation-triangle-fill" class="mr-2" />
            {{ $t('integrations.support.limitReached') }}
        </div>

        <!-- ── LIST VIEW ── -->
        <div v-if="view === 'list'" class="sp-list-view">

            <!-- Segmented tabs -->
            <div class="sp-tabs">
                <button class="sp-tab" :class="{ 'sp-tab-active': statusFilter === 'open' }" @click="filterByStatus('open')">
                    {{ $t('integrations.support.openTickets') }}
                </button>
                <button class="sp-tab" :class="{ 'sp-tab-active': statusFilter === 'closed' }" @click="filterByStatus('closed')">
                    {{ $t('integrations.support.closedTickets') }}
                </button>
            </div>

            <!-- Loading -->
            <div v-if="loadingTickets" class="sp-loading">
                <b-spinner variant="primary" small />
                <span>{{ $t('integrations.support.loading') }}</span>
            </div>

            <!-- Empty state -->
            <div v-else-if="tickets.length === 0" class="sp-empty">
                <div class="sp-empty-icon">
                    <b-icon icon="inbox" font-scale="2.2" />
                </div>
                <p class="sp-empty-text">{{ $t('integrations.support.noTickets') }}</p>
                <b-button variant="primary" size="sm" @click="showCreateForm" :disabled="!canCreateTicket">
                    <b-icon icon="plus" class="mr-1" />{{ $t('integrations.support.newTicket') }}
                </b-button>
            </div>

            <!-- Ticket rows -->
            <div v-else class="sp-ticket-list">
                <div
                    v-for="ticket in tickets"
                    :key="ticket.id"
                    class="sp-ticket-row"
                    :class="'sp-p-' + ticket.priority"
                    @click="openTicket(ticket)"
                >
                    <div class="sp-row-main">
                        <div class="sp-row-top">
                            <span class="sp-row-num">#{{ ticket.ticket_number }}</span>
                            <span class="sp-row-subject">{{ ticket.subject }}</span>
                        </div>
                        <div class="sp-row-chips">
                            <span class="sp-chip" :class="'sp-chip-' + ticket.status">
                                {{ $t('integrations.support.statuses.' + ticket.status) }}
                            </span>
                            <span class="sp-chip sp-chip-neutral">{{ $t('integrations.support.priorities.' + ticket.priority) }}</span>
                            <span class="sp-chip sp-chip-neutral">{{ $t('integrations.support.topics.' + ticket.topic) }}</span>
                            <span v-if="ticket.comment_count > 0" class="sp-chip sp-chip-reply">
                                <b-icon icon="chat-dots" />&nbsp;{{ ticket.comment_count }}
                            </span>
                        </div>
                    </div>
                    <div class="sp-row-aside">
                        <span class="sp-row-date">{{ formatDate(ticket.created_at) }}</span>
                        <span v-if="ticket.comment_count > 0" class="sp-row-upd">
                            <b-icon icon="arrow-repeat" />&nbsp;{{ formatDate(ticket.updated_at) }}
                        </span>
                        <b-icon icon="chevron-right" class="sp-row-arrow" />
                    </div>
                </div>
            </div>

        </div>

        <!-- ── CREATE VIEW ── -->
        <div v-if="view === 'create'" class="sp-form-view">
            <!-- Success -->
            <div v-if="submitted" class="sp-success">
                <div class="sp-success-ring">
                    <b-icon icon="check-circle-fill" font-scale="1.6" />
                </div>
                <h5 class="sp-success-title">{{ $t('integrations.support.successTitle') }}</h5>
                <p class="sp-success-msg">{{ $t('integrations.support.successMessage', { number: ticketNumber }) }}</p>
                <div class="mb-3">
                    <b-badge :variant="tierVariant(lastTier)" class="mr-1 text-capitalize">{{ $t('integrations.support.tiers.' + lastTier) }}</b-badge>
                    <b-badge variant="light" class="text-capitalize">{{ $t('integrations.support.topics.' + lastTopic) }}</b-badge>
                </div>
                <b-button variant="outline-primary" size="sm" @click="backToList">
                    <b-icon icon="arrow-left" class="mr-1" />{{ $t('integrations.support.backToTickets') }}
                </b-button>
            </div>

            <!-- Form -->
            <div v-else class="sp-form-wrap">
                <div class="sp-form-topbar">
                    <button class="sp-back-btn" type="button" @click="backToList">
                        <b-icon icon="arrow-left" />
                    </button>
                    <span class="sp-form-heading">{{ $t('integrations.support.createTicket') }}</span>
                </div>

                <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = null" class="mb-4">
                    {{ error }}
                </b-alert>

                <b-form @submit.prevent="createTicket" class="sp-form">
                    <div class="sp-field">
                        <label class="sp-label">{{ $t('integrations.support.subject') }}</label>
                        <b-form-input
                            v-model="form.subject"
                            :placeholder="$t('integrations.support.subjectPlaceholder')"
                            required
                            maxlength="200"
                        />
                    </div>

                    <div class="sp-field-row">
                        <div class="sp-field">
                            <label class="sp-label">{{ $t('integrations.support.topic') }}</label>
                            <b-form-select v-model="form.topic" :options="topicOptions" required />
                        </div>
                        <div class="sp-field">
                            <label class="sp-label">{{ $t('integrations.support.priority') }}</label>
                            <b-form-select v-model="form.priority" :options="priorityOptions" required />
                        </div>
                    </div>

                    <div class="sp-field sp-field-grow">
                        <label class="sp-label">{{ $t('integrations.support.description') }}</label>
                        <b-form-textarea
                            v-model="form.description"
                            :placeholder="$t('integrations.support.descriptionPlaceholder')"
                            rows="7"
                            required
                            maxlength="5000"
                        />
                    </div>

                    <div class="sp-form-actions">
                        <b-button variant="link" class="text-muted" @click="backToList">{{ $t('integrations.support.cancel') }}</b-button>
                        <b-button type="submit" variant="primary" :disabled="saving || !canCreateTicket">
                            <b-spinner v-if="saving" small class="mr-1" />
                            <b-icon v-else icon="cursor" class="mr-1" />
                            {{ saving ? $t('integrations.support.submitting') : $t('integrations.support.submit') }}
                        </b-button>
                    </div>
                </b-form>
            </div>

        </div>

        <!-- ── DETAIL VIEW ── -->
        <div v-if="view === 'detail'" class="sp-detail-view">

            <div v-if="loadingDetail" class="sp-loading">
                <b-spinner variant="primary" small />
                <span>{{ $t('integrations.support.loading') }}</span>
            </div>

            <template v-else-if="selectedTicket">
                <div class="sp-detail-layout">

                    <!-- Left sidebar: ticket meta -->
                    <aside class="sp-detail-aside">
                        <button class="sp-back-btn sp-back-inline" type="button" @click="backToList">
                            <b-icon icon="arrow-left" />
                            <span>{{ $t('integrations.support.backToTickets') }}</span>
                        </button>

                        <div class="sp-aside-num">#{{ selectedTicket.ticket_number }}</div>
                        <h6 class="sp-aside-subject">{{ selectedTicket.subject }}</h6>

                        <div class="sp-aside-fields">
                            <div class="sp-aside-field">
                                <span class="sp-aside-key">{{ $t('integrations.support.status') }}</span>
                                <b-badge :variant="statusVariant(selectedTicket.status)" class="text-capitalize">
                                    {{ $t('integrations.support.statuses.' + selectedTicket.status) }}
                                </b-badge>
                            </div>
                            <div class="sp-aside-field">
                                <span class="sp-aside-key">{{ $t('integrations.support.priority') }}</span>
                                <span class="sp-aside-val text-capitalize">{{ $t('integrations.support.priorities.' + selectedTicket.priority) }}</span>
                            </div>
                            <div class="sp-aside-field">
                                <span class="sp-aside-key">{{ $t('integrations.support.topic') }}</span>
                                <span class="sp-aside-val text-capitalize">{{ $t('integrations.support.topics.' + selectedTicket.topic) }}</span>
                            </div>
                            <div class="sp-aside-field">
                                <span class="sp-aside-key">{{ $t('integrations.support.created') }}</span>
                                <span class="sp-aside-val">{{ formatDate(selectedTicket.created_at) }}</span>
                            </div>
                        </div>

                        <div class="sp-aside-section">
                            <span class="sp-aside-key sp-aside-key-block">{{ $t('integrations.support.description') }}</span>
                            <p class="sp-aside-desc">{{ selectedTicket.description }}</p>
                        </div>

                        <b-button
                            v-if="canComment"
                            variant="outline-success"
                            size="sm"
                            block
                            :disabled="resolvingTicket"
                            @click="resolveTicket"
                            class="sp-resolve-btn"
                        >
                            <b-spinner v-if="resolvingTicket" small class="mr-1" />
                            <b-icon v-else icon="check-circle" class="mr-1" />
                            {{ resolvingTicket ? $t('integrations.support.resolvingTicket') : $t('integrations.support.resolveTicket') }}
                        </b-button>
                    </aside>

                    <!-- Right: conversation -->
                    <div class="sp-detail-main">
                        <div class="sp-thread-header">
                            <b-icon icon="chat-dots" class="mr-2" />
                            {{ $t('integrations.support.comments') }}
                            <span v-if="comments.length" class="sp-thread-count">{{ comments.length }}</span>
                        </div>

                        <div v-if="comments.length === 0" class="sp-no-comments">
                            <b-icon icon="chat-square-dots" font-scale="1.8" class="mb-2" />
                            <p class="mb-0">{{ $t('integrations.support.noComments') }}</p>
                        </div>

                        <div v-else class="sp-thread">
                            <div
                                v-for="comment in comments"
                                :key="comment.id"
                                class="sp-msg"
                                :class="comment.author_type === 'operator' ? 'sp-msg-op' : 'sp-msg-usr'"
                            >
                                <div class="sp-msg-avatar" :class="comment.author_type === 'operator' ? 'sp-av-op' : 'sp-av-usr'">
                                    {{ comment.author_name ? comment.author_name.charAt(0).toUpperCase() : '?' }}
                                </div>
                                <div class="sp-msg-content">
                                    <div class="sp-msg-meta">
                                        <strong>{{ comment.author_name }}</strong>
                                        <b-badge v-if="comment.author_type === 'operator'" variant="info" class="ml-1 sp-support-badge">
                                            {{ $t('integrations.support.supportTeam') }}
                                        </b-badge>
                                        <span class="sp-msg-time">{{ formatDate(comment.created_at) }}</span>
                                    </div>
                                    <p class="sp-msg-text">{{ comment.content }}</p>
                                </div>
                            </div>
                        </div>

                        <div v-if="canComment" class="sp-reply-area">
                            <b-form @submit.prevent="addComment">
                                <b-form-textarea
                                    v-model="newComment"
                                    :placeholder="$t('integrations.support.commentPlaceholder')"
                                    rows="3"
                                    maxlength="10000"
                                    class="sp-reply-input mb-2"
                                />
                                <div class="d-flex justify-content-end">
                                    <b-button
                                        type="submit"
                                        variant="primary"
                                        size="sm"
                                        :disabled="submittingComment || !newComment.trim()"
                                    >
                                        <b-spinner v-if="submittingComment" small class="mr-1" />
                                        <b-icon v-else icon="reply" class="mr-1" />
                                        {{ submittingComment ? $t('integrations.support.sendingComment') : $t('integrations.support.addComment') }}
                                    </b-button>
                                </div>
                            </b-form>
                        </div>
                    </div>

                </div>
            </template>
        </div>

                <!-- Attachments panel (below main grid) -->
                <template v-if="view === 'detail' && selectedTicket">
                    <div class="sp-attach-panel">
                        <div class="sp-attach-header">
                            <b-icon icon="paperclip" class="mr-2" />
                            {{ $t('integrations.support.attachments') }}
                            <span v-if="attachments.length" class="sp-thread-count ml-2">{{ attachments.length }}</span>
                        </div>
                        <div class="sp-attach-body">
                            <div v-if="attachments.length === 0 && !canComment" class="sp-attach-empty">
                                {{ $t('integrations.support.noAttachments') }}
                            </div>
                            <div v-if="attachments.length > 0" class="sp-attach-list">
                                <div v-for="att in attachments" :key="att.id" class="sp-attach-row">
                                    <b-icon icon="file-earmark" class="mr-2 text-muted" />
                                    <button type="button" class="sp-attach-name" @click="downloadAttachment(att)">{{ att.file_name }}</button>
                                    <span class="sp-attach-size">{{ formatFileSize(att.file_size) }}</span>
                                    <b-spinner v-if="downloadingAttachmentId === att.id" small class="ml-2" />
                                </div>
                            </div>
                            <div v-if="canComment" class="sp-attach-upload">
                                <label v-if="!uploadingFile" class="sp-attach-label">
                                    <b-icon icon="upload" class="mr-1" />
                                    {{ $t('integrations.support.uploadFile') }}
                                    <input type="file" class="sp-attach-input" @change="uploadAttachment" />
                                </label>
                                <div v-else class="sp-attach-uploading">
                                    <b-spinner small class="mr-2" />
                                    {{ $t('integrations.support.uploading') }}
                                </div>
                                <div v-if="uploadError" class="sp-attach-error mt-1">{{ uploadError }}</div>
                            </div>
                        </div>
                    </div>
                </template>

        </div>
</template>

<script>
var TOPICS_BY_TIER = {
    free: ['general'],
    standard: ['general', 'technical'],
    pro: ['general', 'technical', 'billing', 'account', 'priority_support']
}

var PRIORITIES_BY_TIER = {
    free: ['normal'],
    standard: ['low', 'normal', 'high'],
    pro: ['low', 'normal', 'high', 'urgent']
}

export default {
    props: {
        model: {
            type: Object,
            default: function() {
                return null
            }
        },
        active: {
            type: Boolean,
            default: false
        }
    },

    data: function() {
        return {
            view: 'list',
            tierInfo: null,
            tickets: [],
            loadingTickets: false,
            statusFilter: 'open',
            form: {
                subject: '',
                description: '',
                topic: 'general',
                priority: 'normal'
            },
            saving: false,
            submitted: false,
            ticketNumber: null,
            lastTier: null,
            lastTopic: null,
            error: null,
            selectedTicket: null,
            comments: [],
            newComment: '',
            loadingDetail: false,
            submittingComment: false,
                resolvingTicket: false,
                attachments: [],
                uploadingFile: false,
                uploadError: null,
                downloadingAttachmentId: null
        }
    },

    computed: {
        currentTier: function() {
            return this.tierInfo ? this.tierInfo.tier : 'free'
        },

        topicOptions: function() {
            var allowed = TOPICS_BY_TIER[this.currentTier] || ['general']
            var self = this
            return allowed.map(function(t) {
                return { value: t, text: self.$t('integrations.support.topics.' + t) }
            })
        },

        priorityOptions: function() {
            var allowed = PRIORITIES_BY_TIER[this.currentTier] || ['normal']
            var self = this
            return allowed.map(function(p) {
                return { value: p, text: self.$t('integrations.support.priorities.' + p) }
            })
        },

        limitReached: function() {
            if (!this.tierInfo || !this.tierInfo.limits || !this.tierInfo.usage) return false
            var limits = this.tierInfo.limits
            var usage = this.tierInfo.usage
            if (limits.day > 0 && usage.day >= limits.day) return true
            if (limits.week > 0 && usage.week >= limits.week) return true
            if (limits.month > 0 && usage.month >= limits.month) return true
            return false
        },

        canCreateTicket: function() {
            if (this.currentTier === 'free' && !this.$store.getters.isAdmin) return false
            return !this.limitReached
        },

        canComment: function() {
            if (!this.selectedTicket) return false
            var s = this.selectedTicket.status
            return s !== 'closed' && s !== 'resolved'
        }
    },

    mounted: function() {
        // Set defaults immediately so the UI renders, then fetch real data
        this.tierInfo = { tier: 'free', limits: { day: 2, week: 5, month: 10 }, usage: { day: 0, week: 0, month: 0 } }
        this.loadTierInfo()
        this.loadTickets()
    },

    watch: {
        active: function(val) {
            if (val && !this.tierInfo) {
                this.loadTierInfo()
            }
        }
    },

    methods: {
        loadTierInfo: function() {
            var self = this
            self.$integrationApi.get('/protokol-support/support/info').then(function(resp) {
                self.tierInfo = resp.data
                var allowed = TOPICS_BY_TIER[self.currentTier] || ['general']
                if (allowed.indexOf(self.form.topic) === -1) {
                    self.form.topic = allowed[0]
                }
            }).catch(function() {
                // fail silently — tier defaults to free via currentTier computed
            })
        },

        loadTickets: function() {
            var self = this
            self.loadingTickets = true
            self.$integrationApi.get('/protokol-support/support/tickets', {
                params: { status: self.statusFilter }
            }).then(function(resp) {
                self.tickets = resp.data && resp.data.tickets ? resp.data.tickets : []
                self.loadingTickets = false
            }).catch(function() {
                self.tickets = []
                self.loadingTickets = false
            })
        },

        filterByStatus: function(status) {
            this.statusFilter = status
            this.loadTickets()
        },

        showCreateForm: function() {
            this.view = 'create'
            this.submitted = false
            this.error = null
        },

        backToList: function() {
            this.view = 'list'
            this.submitted = false
            this.error = null
            this.selectedTicket = null
            this.comments = []
            this.newComment = ''
                this.attachments = []
                this.uploadingFile = false
                this.uploadError = null
                this.downloadingAttachmentId = null
            this.form = {
                subject: '',
                description: '',
                topic: 'general',
                priority: 'normal'
            }
            this.loadTickets()
            this.loadTierInfo()
        },

        openTicket: function(ticket) {
            var self = this
            self.selectedTicket = ticket
            self.comments = []
            self.newComment = ''
            self.loadingDetail = true
            self.view = 'detail'

            self.$integrationApi.get('/protokol-support/support/tickets/' + ticket.id).then(function(resp) {
                self.selectedTicket = resp.data || ticket
                self.comments = resp.data && resp.data.comments ? resp.data.comments : []
                    self.attachments = resp.data && resp.data.attachments ? resp.data.attachments : []
                self.loadingDetail = false

                // Mark the ticket as read
                self.$integrationApi.post('/protokol-support/support/tickets/' + ticket.id + '/mark-read').then(function() {
                    // Refresh the unread badge count
                    self.$integrationApi.get('/protokol-support/support/unread-count').then(function(res) {
                        var count = res.data && res.data.unread_count ? res.data.unread_count : 0
                        self.$store.commit('SET_INTEGRATION_BADGE', { id: 'protokol-support', count: count })
                    }).catch(function() {})
                }).catch(function() {})
            }).catch(function() {
                self.loadingDetail = false
            })
        },

        addComment: function() {
            var self = this
            if (!self.newComment.trim() || !self.selectedTicket) return
            self.submittingComment = true

            self.$integrationApi.post('/protokol-support/support/tickets/' + self.selectedTicket.id + '/comments', {
                content: self.newComment
            }).then(function(resp) {
                self.submittingComment = false
                if (resp.data) {
                    self.comments.push(resp.data)
                }
                self.newComment = ''
            }).catch(function() {
                self.submittingComment = false
            })
        },

        resolveTicket: function() {
            var self = this
            if (!self.selectedTicket) return
            if (!confirm(self.$t('integrations.support.resolveConfirm'))) return
            self.resolvingTicket = true

            self.$integrationApi.post('/protokol-support/support/tickets/' + self.selectedTicket.id + '/resolve').then(function(resp) {
                self.resolvingTicket = false
                if (resp.data && resp.data.status) {
                    self.selectedTicket.status = resp.data.status
                }
            }).catch(function() {
                self.resolvingTicket = false
            })
        },

        usageRemaining: function(period) {
            if (!this.tierInfo || !this.tierInfo.limits || !this.tierInfo.usage) {
                return '∞'
            }
            var limit = this.tierInfo.limits[period]
            if (limit === 0) return '∞'
            var used = this.tierInfo.usage[period] || 0
            var remaining = limit - used
            if (remaining < 0) remaining = 0
            return remaining + ' / ' + limit
        },

        createTicket: function() {
            var self = this
            self.saving = true
            self.error = null

            self.$integrationApi.post('/protokol-support/support/tickets', {
                subject: self.form.subject,
                description: self.form.description,
                topic: self.form.topic,
                priority: self.form.priority
            }).then(function(resp) {
                self.saving = false
                self.submitted = true
                self.ticketNumber = resp.data && resp.data.ticket_number ? resp.data.ticket_number : null
                self.lastTier = self.currentTier
                self.lastTopic = self.form.topic
                self.loadTierInfo()
            }).catch(function(err) {
                self.saving = false
                var code = err.response && err.response.data && err.response.data.code
                if (code === 4029) {
                    self.error = self.$t('integrations.support.limitExceeded')
                    self.loadTierInfo()
                } else if (err.response && err.response.data && err.response.data.message) {
                    self.error = err.response.data.message
                } else {
                    self.error = self.$t('integrations.support.errorGeneric')
                }
            })
        },

        tierVariant: function(tier) {
            if (tier === 'pro') return 'warning'
            if (tier === 'standard') return 'info'
            return 'secondary'
        },

        statusVariant: function(status) {
            if (status === 'open') return 'primary'
            if (status === 'in_progress') return 'info'
            if (status === 'waiting_on_user') return 'warning'
            if (status === 'resolved') return 'success'
            if (status === 'closed') return 'secondary'
            return 'light'
        },

        capitalize: function(str) {
            if (!str) return ''
            return str.charAt(0).toUpperCase() + str.slice(1)
        },

        isLimitHit: function(period) {
            if (!this.tierInfo || !this.tierInfo.limits || !this.tierInfo.usage) return false
            var limit = this.tierInfo.limits[period]
            if (limit === 0) return false
            return this.tierInfo.usage[period] >= limit
        },

        formatDate: function(dateStr) {
            if (!dateStr) return ''
            var d = new Date(dateStr)
            var day = d.getDate()
            var month = d.getMonth() + 1
            var year = d.getFullYear()
            var hours = d.getHours()
            var minutes = d.getMinutes()
            if (day < 10) day = '0' + day
            if (month < 10) month = '0' + month
            if (hours < 10) hours = '0' + hours
            if (minutes < 10) minutes = '0' + minutes
            return day + '.' + month + '.' + year + ' ' + hours + ':' + minutes
            },

            uploadAttachment: function(e) {
                var file = e.target.files && e.target.files[0]
                if (!file || !this.selectedTicket) return
                var self = this
                var fd = new FormData()
                fd.append('file', file)
                self.uploadingFile = true
                self.uploadError = null
                self.$integrationApi.post('/protokol-support/support/tickets/' + self.selectedTicket.id + '/attachments', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }).then(function(resp) {
                    self.uploadingFile = false
                    if (resp.data) {
                        self.attachments.push(resp.data)
                    }
                    e.target.value = ''
                }).catch(function() {
                    self.uploadingFile = false
                    self.uploadError = self.$t('integrations.support.uploadError')
                    e.target.value = ''
                })
            },

            downloadAttachment: function(att) {
                var self = this
                if (!self.selectedTicket) return
                self.downloadingAttachmentId = att.id
                self.$integrationApi.get('/protokol-support/support/tickets/' + self.selectedTicket.id + '/attachments/' + att.id + '/download', {
                    responseType: 'blob'
                }).then(function(resp) {
                    self.downloadingAttachmentId = null
                    var url = window.URL.createObjectURL(resp.data)
                    var a = document.createElement('a')
                    a.href = url
                    a.download = att.file_name
                    a.click()
                    window.URL.revokeObjectURL(url)
                }).catch(function() {
                    self.downloadingAttachmentId = null
                })
            },

            formatFileSize: function(bytes) {
                if (!bytes || bytes === 0) return '0 B'
                var k = 1024
                var units = ['B', 'KB', 'MB', 'GB']
                var i = Math.floor(Math.log(bytes) / Math.log(k))
                if (i < 0) i = 0
                if (i >= units.length) i = units.length - 1
                return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + units[i]
            }
    }
}
</script>

<style scoped lang="scss">
$primary: var(--primary, #206bc4);
$border: var(--border-default, #e5e7eb);
$text-muted: var(--text-muted, #6b7280);
$radius: 8px;

// Priority palette
$p-urgent: #ef4444;
$p-high: #f97316;
$p-normal: #3b82f6;
$p-low: #9ca3af;

/* ── SHELL ────────────────────────────────────── */
.sp-shell {
    max-width: 960px;
    margin: 0 auto;
    font-size: 0.875rem;
}

/* ── HEADER BAR ───────────────────────────────── */
.sp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 16px 0;
    border-bottom: 1px solid $border;
    margin-bottom: 0;
    gap: 12px;
    flex-wrap: wrap;
}

.sp-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;
}

.sp-header-title {
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-primary, #111827);
    white-space: nowrap;
}

.sp-header-sep {
    display: inline-block;
    width: 1px;
    height: 16px;
    background: $border;
    flex-shrink: 0;
}

.sp-tier-badge {
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    padding: 0.25em 0.6em;
}

.sp-quota-row {
    display: flex;
    align-items: center;
    gap: 14px;
}

.sp-quota-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.2;

    strong {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--text-secondary, #374151);
    }

    em {
        font-style: normal;
        font-size: 0.67rem;
        color: $text-muted;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &.sp-quota-danger strong {
        color: #ef4444;
    }
}

.sp-limit-banner {
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.25);
    border-radius: $radius;
    padding: 8px 14px;
    color: #f59e0b;
    font-size: 0.8rem;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
}

/* ── TABS ────────────────────────────────────── */
.sp-tabs {
    display: flex;
    gap: 0;
    border-bottom: 2px solid $border;
    margin-bottom: 16px;
    margin-top: 16px;
}

.sp-tab {
    background: none;
    border: none;
    padding: 8px 16px;
    font-size: 0.82rem;
    font-weight: 500;
    color: $text-muted;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: color 0.15s, border-color 0.15s;
    outline: none;

    &:hover {
        color: var(--text-secondary, #374151);
    }

    &.sp-tab-active {
        color: var(--primary, #206bc4);
        border-bottom-color: var(--primary, #206bc4);
    }
}

/* ── LOADING / EMPTY ─────────────────────────── */
.sp-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
    padding: 48px 0;
    color: $text-muted;
}

.sp-empty {
    text-align: center;
    padding: 56px 0;

    .sp-empty-icon {
        width: 60px;
        height: 60px;
        margin: 0 auto 16px;
        border-radius: 50%;
        background: var(--bg-card-secondary, #f3f4f6);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-muted, #9ca3af);
    }

    .sp-empty-text {
        color: $text-muted;
        margin-bottom: 16px;
    }
}

/* ── TICKET LIST ─────────────────────────────── */
.sp-ticket-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sp-ticket-row {
    display: flex;
    align-items: stretch;
    background: var(--bg-card, #fff);
    border: 1px solid $border;
    border-radius: $radius;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.15s, border-color 0.15s;
    border-left: 4px solid $p-normal;

    &:hover {
        border-color: var(--border-hover, #d1d5db);
        box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.06));
    }

    &.sp-p-urgent { border-left-color: $p-urgent; }
    &.sp-p-high   { border-left-color: $p-high; }
    &.sp-p-normal { border-left-color: $p-normal; }
    &.sp-p-low    { border-left-color: $p-low; }
}

.sp-row-main {
    flex: 1;
    padding: 12px 14px;
    min-width: 0;
}

.sp-row-top {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
}

.sp-row-num {
    font-family: monospace;
    font-size: 0.75rem;
    color: $text-muted;
    white-space: nowrap;
    flex-shrink: 0;
}

.sp-row-subject {
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--text-primary, #111827);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sp-row-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.sp-chip {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    padding: 2px 7px;
    border-radius: 4px;
    font-weight: 500;
    text-transform: capitalize;

    &.sp-chip-open           { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
    &.sp-chip-in_progress    { background: rgba(14, 165, 233, 0.15); color: #0ea5e9; }
    &.sp-chip-waiting_on_user{ background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
    &.sp-chip-resolved       { background: rgba(34, 197, 94, 0.15); color: #22c55e; }
    &.sp-chip-closed         { background: var(--bg-card-secondary, #f3f4f6); color: var(--text-muted, #6b7280); }
    &.sp-chip-neutral        { background: var(--bg-card-secondary, #f3f4f6); color: var(--text-muted, #6b7280); }
    &.sp-chip-reply          { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
}

.sp-row-aside {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    padding: 12px 12px 12px 8px;
    min-width: 120px;
    flex-shrink: 0;
}

.sp-row-date {
    font-size: 0.72rem;
    color: $text-muted;
    white-space: nowrap;
}

.sp-row-upd {
    font-size: 0.68rem;
    color: var(--text-muted, #9ca3af);
    margin-top: 2px;
    white-space: nowrap;
}

.sp-row-arrow {
    color: var(--text-muted, #d1d5db);
    margin-top: 6px;
}

/* ── SHARED: back button ─────────────────────── */
.sp-back-btn {
    background: none;
    border: none;
    padding: 0;
    color: $text-muted;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    transition: color 0.15s;
    outline: none;

    &:hover { color: var(--text-primary, #111827); }
}

.sp-back-inline {
    margin-bottom: 20px;
    font-weight: 500;
}

/* ── CREATE VIEW ─────────────────────────────── */
.sp-form-view {
    padding-top: 16px;
}

.sp-form-topbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid $border;
}

.sp-form-heading {
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-primary, #111827);
}

.sp-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.sp-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
    }
}

.sp-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.sp-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--text-secondary, #374151);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
}

.sp-form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    padding-top: 4px;
}

/* ── SUCCESS ─────────────────────────────────── */
.sp-success {
    text-align: center;
    padding: 64px 24px;

    .sp-success-ring {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        background: rgba(34, 197, 94, 0.12);
        color: #22c55e;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;
    }

    .sp-success-title {
        font-weight: 600;
        margin-bottom: 8px;
        color: var(--text-primary, #111827);
    }

    .sp-success-msg {
        color: $text-muted;
        margin-bottom: 20px;
    }
}

/* ── DETAIL VIEW ─────────────────────────────── */
.sp-detail-view {
    padding-top: 16px;
}

.sp-detail-layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 20px;
    align-items: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

/* Aside */
.sp-detail-aside {
    background: var(--bg-card-secondary, #f9fafb);
    border: 1px solid $border;
    border-radius: $radius;
    padding: 20px;
    position: sticky;
    top: 16px;
}

.sp-aside-num {
    font-family: monospace;
    font-size: 0.75rem;
    color: $text-muted;
    margin-bottom: 4px;
}

.sp-aside-subject {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--text-primary, #111827);
    margin-bottom: 16px;
    line-height: 1.4;
}

.sp-aside-fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid $border;
}

.sp-aside-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.sp-aside-key {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted, #9ca3af);
    flex-shrink: 0;
}

.sp-aside-key-block {
    display: block;
    margin-bottom: 6px;
}

.sp-aside-val {
    font-size: 0.8rem;
    color: var(--text-secondary, #374151);
    text-align: right;
}

.sp-aside-section {
    margin-bottom: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid $border;
}

.sp-aside-desc {
    font-size: 0.8rem;
    color: var(--text-secondary, #4b5563);
    white-space: pre-wrap;
    line-height: 1.5;
    margin: 0;
}

.sp-resolve-btn {
    width: 100%;
}

/* Main conversation panel */
.sp-detail-main {
    background: var(--bg-card, #fff);
    border: 1px solid $border;
    border-radius: $radius;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.sp-thread-header {
    display: flex;
    align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid $border;
    font-weight: 600;
    font-size: 0.82rem;
    color: var(--text-secondary, #374151);
    background: var(--bg-card-secondary, #f9fafb);
}

.sp-thread-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-card-secondary, #e5e7eb);
    color: var(--text-secondary, #374151);
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 1px 7px;
}

.sp-no-comments {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: var(--text-muted, #9ca3af);
    text-align: center;
}

.sp-thread {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sp-msg {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

.sp-msg-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.75rem;
    flex-shrink: 0;
    color: #fff;

    &.sp-av-usr { background: #6366f1; }
    &.sp-av-op  { background: #0284c7; }
}

.sp-msg-content {
    flex: 1;
    min-width: 0;
}

.sp-msg-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 0.78rem;
    color: var(--text-secondary, #374151);
}

.sp-msg-time {
    font-size: 0.7rem;
    color: $text-muted;
    margin-left: 4px;
}

.sp-support-badge {
    font-size: 0.65rem;
}

.sp-msg-text {
    font-size: 0.82rem;
    color: var(--text-secondary, #374151);
    white-space: pre-wrap;
    line-height: 1.55;
    margin: 0;
    background: var(--bg-card-secondary, #f9fafb);
    border: 1px solid $border;
    border-radius: 6px;
    padding: 10px 12px;

    .sp-msg-op & {
        background: rgba(59, 130, 246, 0.08);
        border-color: rgba(59, 130, 246, 0.2);
    }
}

.sp-reply-area {
    padding: 14px 18px;
    border-top: 1px solid $border;
    background: var(--bg-card-secondary, #f9fafb);
}

.sp-reply-input {
    font-size: 0.85rem;
}

    /* ── ATTACHMENTS ─────────────────────────────── */
    .sp-attach-panel {
        background: var(--bg-card, #fff);
        border: 1px solid $border;
        border-radius: $radius;
        overflow: hidden;
        margin-top: 20px;
    }

    .sp-attach-header {
        display: flex;
        align-items: center;
        padding: 14px 18px;
        border-bottom: 1px solid $border;
        font-weight: 600;
        font-size: 0.82rem;
        color: var(--text-secondary, #374151);
        background: var(--bg-card-secondary, #f9fafb);
    }

    .sp-attach-body {
        padding: 14px 18px;
    }

    .sp-attach-empty {
        font-size: 0.82rem;
        color: $text-muted;
    }

    .sp-attach-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 12px;
    }

    .sp-attach-row {
        display: flex;
        align-items: center;
        font-size: 0.82rem;
        gap: 4px;
    }

    .sp-attach-name {
        background: none;
        border: none;
        padding: 0;
        color: var(--primary, #206bc4);
        cursor: pointer;
        font-size: 0.82rem;
        text-decoration: underline;
        outline: none;

        &:hover {
            color: var(--primary-dark, #1a56a8);
        }
    }

    .sp-attach-size {
        font-size: 0.72rem;
        color: $text-muted;
        margin-left: 6px;
    }

    .sp-attach-upload {
        margin-top: 8px;
    }

    .sp-attach-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        cursor: pointer;
        background: none;
        border: 1px dashed $border;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 0.8rem;
        color: var(--text-secondary, #374151);
        transition: border-color 0.15s, color 0.15s;
        margin: 0;

        &:hover {
            border-color: var(--primary, #206bc4);
            color: var(--primary, #206bc4);
        }
    }

    .sp-attach-input {
        display: none;
    }

    .sp-attach-uploading {
        display: inline-flex;
        align-items: center;
        font-size: 0.8rem;
        color: $text-muted;
    }

    .sp-attach-error {
        font-size: 0.78rem;
        color: #ef4444;
    }
</style>
